import { supabase } from '@/lib/supabase';

/**
 * Correos de los formularios del sitio hacia la AUTOMOTORA / el VENDEDOR.
 *
 * Antes el navegador mandaba `to` + asunto + HTML ya armados a la edge
 * `send-email`, y la edge los aceptaba anónimos si los destinatarios estaban
 * "registrados": cualquiera podía mandar HTML arbitrario desde @goauto.cl a
 * esas casillas (phishing). Ahora el sitio manda QUÉ formulario fue
 * (`site_form.kind`) + los datos crudos (`params`, los mismos argumentos que
 * recibe la plantilla de acá), y la edge resuelve destinatarios, asunto y HTML
 * server-side.
 *
 * Respaldo: si el modo nuevo falla se intenta el correo VIEJO, pero la edge ya no
 * lo acepta sin sesión (pentest 30-oct) y da 401: el aviso a la automotora no sale
 * y el error solo queda en consola. El lead se guarda igual.
 *
 * Los correos al VENDEDOR no tienen respaldo (`legacy` = null): el navegador ya no
 * conoce su casilla, anon no puede leer `users`. Solo la edge la resuelve, desde
 * `vehicle_id`.
 *
 * Las confirmaciones al CLIENTE FINAL (casilla del visitante) NO pasan por acá.
 */
export type SiteFormKind =
  | 'booking_dealer'
  | 'booking_seller'
  | 'cancel_dealer'
  | 'cancel_seller'
  | 'lead_buy'
  | 'lead_consign'
  | 'lead_search'
  | 'lead_dynamic'
  | 'lead_finance'
  | 'lead_finance_seller'
  | 'contact';

export interface SiteFormRequest {
  client_id: number;
  kind: SiteFormKind;
  vehicle_id?: number | null;
  params: object;
}

/** Body del envío viejo (el mismo que reciben los `sendEmail` de send-email.ts / send-email2.ts). */
export interface LegacyEmailOptions {
  to: string[];
  subject: string;
  content: string;
  from?: string;
  template_id?: string;
  template_data?: Record<string, any>;
  resolve_vehicle_plate?: number;
}

type EmailResult = { success: boolean; error?: string; skipped?: boolean };

const idValido = (n: unknown): n is number =>
  typeof n === 'number' && Number.isInteger(n) && n > 0;

/** Id numérico (o null) a partir de lo que traiga el store/la fila: '123', 123, undefined… */
export const idNumerico = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return idValido(n) ? n : null;
};

export async function sendSiteFormEmail(
  sf: SiteFormRequest,
  legacy: LegacyEmailOptions | null,
  legacySend?: (options: LegacyEmailOptions) => Promise<EmailResult>
): Promise<EmailResult> {
  if (idValido(sf.client_id)) {
    try {
      const siteForm: Record<string, unknown> = {
        client_id: sf.client_id,
        kind: sf.kind,
        params: sf.params,
      };
      if (idValido(sf.vehicle_id)) siteForm.vehicle_id = sf.vehicle_id;

      const { data, error } = await supabase.functions.invoke('send-email', {
        body: { site_form: siteForm },
      });

      if (
        !error &&
        data &&
        typeof data === 'object' &&
        (data as { site_form?: unknown }).site_form === true
      ) {
        // `sent: n` o `skipped: '...'` (sin destinatarios, etc.): lo decidió la
        // edge; no se reintenta por el camino viejo.
        return (data as { skipped?: unknown }).skipped
          ? { success: true, skipped: true }
          : { success: true };
      }
      console.warn('site_form no disponible:', error ?? data);
    } catch (e) {
      console.warn('site_form falló:', e);
    }
  }
  if (!legacy || !legacySend) return { success: false, error: 'site_form no disponible' };
  return legacySend(legacy);
}
